import {
  WebSocketClient,
  WebSocketEvent,
  SocketActionTypes,
} from "@plutoxyz/frame-js";
import plutoSdk from "../plutoSdk";
import { useAuthStore } from "../store/authStore";
import { useDemoStore } from "../store/demoStore";
import {
  handleRequestPrompt,
  handleScriptLog,
  handleRequestOauth,
  handleDone,
  handleClose,
  handleError,
  handleFinished,
  handleTimeout,
  handleMetadata,
  handleUserNetwork,
  handleUserNetworkDone,
  handleCdpTarget,
} from "./handlers";

// Define the config interface locally if it's not exported yet
interface WebSocketClientConfig {
  script: string;
  hostOrigin: string;
  getFingerprint: () => Promise<string> | string;
  socketEndpoint?: string;
  notaryEndpoint?: string;
}

let webSocketClient: WebSocketClient | null = null;

class WebSocketClientWrapper {
  private isInitialized = false;

  init() {
    if (this.isInitialized) {
      console.warn("WebSocketClient already initialized");
      return;
    }

    // Handle async initialization internally to match old interface
    this.initAsync().catch((error) => {
      console.error("Failed to initialize WebSocketClient:", error);
    });

    this.isInitialized = true;
  }

  private async initAsync() {
    const demoState = useDemoStore.getState();

    // Create the enhanced WebSocketClient with configuration
    const config: WebSocketClientConfig = {
      script: demoState.script,
      hostOrigin: plutoSdk.hostOrigin,
      getFingerprint: () => plutoSdk.getFingerprint(),
      socketEndpoint: demoState.endpoints.socket,
      notaryEndpoint: demoState.endpoints.notary,
    };

    webSocketClient = new WebSocketClient(config);

    // Initialize and get session ID
    const sessionId = await webSocketClient.init();

    // Update the auth store with the session ID (like old client did)
    useAuthStore.setState({ sessionId });

    // Connect and set up event handlers
    webSocketClient.connect();

    webSocketClient.on(WebSocketEvent.MESSAGE, (event) => {
      const messageEvent = event as MessageEvent;
      const _data = JSON.parse(messageEvent.data);
      if (!_data || !_data.payload || !_data.payload.type) return;

      const { type, data: payload } = _data.payload;

      // Route messages to existing handlers (preserves all existing logic)
      switch (type as SocketActionTypes) {
        case SocketActionTypes.REQUEST_PROMPT:
          handleRequestPrompt(payload);
          break;
        case SocketActionTypes.SCRIPT_LOG:
          handleScriptLog(payload);
          break;
        case SocketActionTypes.REQUEST_OAUTH:
          handleRequestOauth(payload);
          break;
        case SocketActionTypes.DONE:
          handleDone(payload);
          break;
        case SocketActionTypes.CLOSE:
          handleClose();
          break;
        case SocketActionTypes.ERROR:
          handleError(payload);
          break;
        case SocketActionTypes.FINISHED:
          handleFinished(payload);
          this.disconnect(); // Disconnect after finished (like old client)
          break;
        case SocketActionTypes.TIMEOUT:
          handleTimeout();
          break;
        case SocketActionTypes.METADATA:
          handleMetadata(payload);
          break;
        case SocketActionTypes.REQUEST_USER_NETWORK:
          handleUserNetwork(payload);
          break;
        case SocketActionTypes.REQUEST_USER_NETWORK_DONE:
          handleUserNetworkDone();
          break;
        case SocketActionTypes.CDP_TARGET:
          handleCdpTarget(payload);
          break;
        default:
          console.warn("Received message of unknown type", type);
          return;
      }
    });

    webSocketClient.on(WebSocketEvent.OPEN, () => {
      console.debug("WebSocketClient connected");
    });

    webSocketClient.on(WebSocketEvent.CLOSE, (event) => {
      const closeEvent = event as CloseEvent;
      console.debug("WebSocketClient connection closed");
      // Handle cleanup when connection fails (like old client)
      if (!closeEvent.wasClean) {
        useAuthStore.getState().deepClearSession();
      }
    });

    webSocketClient.on(WebSocketEvent.ERROR, (event) => {
      console.error("WebSocketClient connection error:", event);
    });
  }

  disconnect() {
    if (webSocketClient) {
      webSocketClient.disconnect();
    }
  }

  // Provide compatibility methods if needed
  getSessionId(): string {
    return webSocketClient?.getSessionId() || "";
  }

  getWasSessionRestored(): boolean {
    return webSocketClient?.getWasSessionRestored() || false;
  }
}

// Export singleton instance (same pattern as old client)
export default new WebSocketClientWrapper();
